import * as THREE from 'three';
import { partMotionBaseFrame, partMotionModuleDelta } from './partMotionTransform';
import type { CanonicalEntity } from '@/shared/api/types';
import {
  deriveModuleFrame,
  moduleLocalDirectionToWorldVector,
  moduleLocalToWorldPoint,
} from './lib/moduleFrame';

export type MotionKind = 'door' | 'drawer';

// Use the renderer's own capability owner, not a second UI interpretation of
// source labels. Public source-native presentation is a separate explicit view.
export function sketchupMotionUnavailable(bundle: {
  sourceKind?: string;
  canonicalScene?: { entities?: CanonicalEntity[] };
} | undefined): boolean {
  return bundle?.sourceKind === 'sketchup_decode'
    && !(bundle.canonicalScene?.entities ?? []).some((entity) => new FurnitureMotionObject(entity).canAnimate());
}

export const SKETCHUP_MOTION_UNAVAILABLE = 'Внутренний Viewer: движение SketchUp не подтверждено. Анимация исходной модели доступна в клиентской ссылке из КП.';
export type AxisIdx = 0 | 1 | 2;

export type FurnitureAnimationPlan = {
  kind: MotionKind;
  axisIdx: AxisIdx;
  rotationAxis: THREE.Vector3 | null;
  translationAxis: THREE.Vector3 | null;
  anchorWorld: THREE.Vector3 | null;
  openMagnitude: number;
  direction: 1 | -1;
};

export type MotionCapability =
  | { ok: true; code: 'MOTION_CAPABILITY_READY'; kind: MotionKind }
  | { ok: false; code: 'MOTION_DISABLED_NEEDS_REVIEW' | 'MOTION_CONTRACT_MISSING' | 'MOTION_OWNER_UNSUPPORTED' | 'MOTION_DIRECTION_REQUIRED' | 'MOTION_DOOR_HINGE_REQUIRED' | 'MOTION_DOOR_PIVOT_REQUIRED' | 'MOTION_DRAWER_AXIS_REQUIRED' };

function domainObject(entity?: CanonicalEntity): Record<string, unknown> {
  const domain = entity?.domain;
  return domain && typeof domain === 'object' ? domain as Record<string, unknown> : {};
}

function motionPoint(value: unknown): THREE.Vector3 | null {
  if (!Array.isArray(value) || value.length < 3) return null;
  const coords = value.slice(0, 3).map(Number);
  if (coords.some((item) => !Number.isFinite(item))) return null;
  return new THREE.Vector3(coords[0], coords[1], coords[2]);
}

export function axisUnit(axisIdx: AxisIdx): THREE.Vector3 {
  const v = new THREE.Vector3();
  v.setComponent(axisIdx, 1);
  return v;
}

function parentLocalDirection(parent: THREE.Object3D, worldDirection: THREE.Vector3): THREE.Vector3 {
  const parentWorldQuat = new THREE.Quaternion();
  parent.getWorldQuaternion(parentWorldQuat);
  return worldDirection.clone().applyQuaternion(parentWorldQuat.invert()).normalize();
}

/**
 * Object-owned animation/motion contract for a facade or drawer front.
 *
 * UI and renderer code pass the current view/open command into this object.
 * They must not infer opening semantics from names, bounding boxes or ad-hoc
 * role checks; the concrete front instance owns direction, pivot and axes.
 */
export class FurnitureMotionObject {
  readonly entity: CanonicalEntity;
  readonly moduleById: Map<string, CanonicalEntity>;
  readonly motion: Record<string, unknown>;

  constructor(entity: CanonicalEntity, moduleById: Map<string, CanonicalEntity> = new Map()) {
    this.entity = entity;
    this.moduleById = moduleById;
    const contract = domainObject(entity).motionContract;
    this.motion = contract && typeof contract === 'object' ? contract as Record<string, unknown> : {};
  }

  get kind(): MotionKind | null {
    if (domainObject(this.entity).motionDisabled === true) return null;
    if (!Object.keys(this.motion).length) return null;
    const owner = String(this.motion.owner || '').toLowerCase();
    const type = String(this.motion.type || this.motion.openMode || '').toLowerCase();
    if (owner === 'drawer_front' || type === 'drawer' || type === 'sliding') return 'drawer';
    if (owner === 'facade' || type === 'hinged' || type === 'lift' || type === 'drop_down') return 'door';
    return null;
  }

  canAnimate(): boolean {
    return this.capability().ok;
  }

  /** Named, UI-safe capability boundary. Invalid motion never becomes a no-op. */
  capability(): MotionCapability {
    const domain = domainObject(this.entity);
    if (domain.motionDisabled === true) return { ok: false, code: 'MOTION_DISABLED_NEEDS_REVIEW' };
    if (!Object.keys(this.motion).length) return { ok: false, code: 'MOTION_CONTRACT_MISSING' };
    const kind = this.kind;
    if (!kind) return { ok: false, code: 'MOTION_OWNER_UNSUPPORTED' };
    if (!this.direction) return { ok: false, code: 'MOTION_DIRECTION_REQUIRED' };
    if (kind === 'drawer') {
      const axis = this.motion.slideAxis ?? this.motion.localSlideAxis;
      return typeof axis === 'string' && ['x', 'y', 'z'].includes(axis.trim().toLowerCase())
        ? { ok: true, code: 'MOTION_CAPABILITY_READY', kind }
        : { ok: false, code: 'MOTION_DRAWER_AXIS_REQUIRED' };
    }
    const hingeSide = String(this.motion.hingeSide || '').toLowerCase();
    if (!['left', 'right', 'top', 'bottom'].includes(hingeSide)) {
      return { ok: false, code: 'MOTION_DOOR_HINGE_REQUIRED' };
    }
    if (!motionPoint(this.motion.localPivotMm)) return { ok: false, code: 'MOTION_DOOR_PIVOT_REQUIRED' };
    return { ok: true, code: 'MOTION_CAPABILITY_READY', kind };
  }

  get module(): CanonicalEntity | undefined {
    const domain = domainObject(this.entity);
    const direct = this.entity.moduleId
      || String(domain.moduleId || domain.moduleInstanceId || '')
      || this.entity.parentId
      || '';
    if (direct && this.moduleById.has(direct)) return this.moduleById.get(direct);
    // Fallback: a front's id encodes its owning module as the prefix before the
    // first "::" (e.g. "module_…:left_0::fill::facades-001::facade-1"). Fronts
    // frequently carry no moduleId and a parentId that points at the fill
    // assembly, not the module — without this the module frame can't be found,
    // so a rotated (yaw≠0) module's door/drawer animates in the UNROTATED frame
    // and opens the wrong way. The module map is keyed by full entity id, and
    // module ids use single ":" so splitting on "::" is safe.
    const ownerFromId = String(this.entity.id || '').split('::')[0];
    if (ownerFromId && this.moduleById.has(ownerFromId)) return this.moduleById.get(ownerFromId);
    return direct ? this.moduleById.get(direct) : undefined;
  }

  get usesModuleSpace(): boolean {
    const space = String(this.motion.space || this.motion.frame || '').toLowerCase();
    return space === 'module_local' || space === 'module';
  }

  get direction(): 1 | -1 | null {
    const raw = String(this.motion.direction || this.motion.openDirection || '').toLowerCase();
    if (raw === 'negative') return -1;
    if (raw === 'positive') return 1;
    return null;
  }

  axisIdx(value: unknown, fallback: AxisIdx): AxisIdx {
    const axis = String(value || '').toLowerCase();
    if (axis === 'x') return 0;
    if (axis === 'y') return 1;
    if (axis === 'z') return 2;
    return fallback;
  }

  axisInParent(parent: THREE.Object3D, axisIdx: AxisIdx): THREE.Vector3 {
    const localAxis = axisUnit(axisIdx);
    if (!this.usesModuleSpace) return localAxis;
    const world = moduleLocalDirectionToWorldVector(
      [localAxis.x, localAxis.y, localAxis.z],
      deriveModuleFrame(this.module),
    );
    return parentLocalDirection(parent, new THREE.Vector3(world[0], world[1], world[2]));
  }

  pivotWorld(): THREE.Vector3 | null {
    const raw = motionPoint(this.motion.localPivotMm);
    if (!raw) return null;
    if (!this.usesModuleSpace) return raw;
    const world = moduleLocalToWorldPoint([raw.x, raw.y, raw.z], deriveModuleFrame(this.module));
    return new THREE.Vector3(world[0], world[1], world[2]);
  }

  isLiftDoor(): boolean {
    const hingeSide = String(this.motion.hingeSide || '').toLowerCase();
    return String(this.motion.type || this.motion.openMode || '').toLowerCase() === 'lift' || hingeSide === 'top';
  }

  isDropDownDoor(): boolean {
    const hingeSide = String(this.motion.hingeSide || '').toLowerCase();
    return String(this.motion.type || this.motion.openMode || '').toLowerCase() === 'drop_down' || hingeSide === 'bottom';
  }

  doorOpenMagnitude(): number {
    const contractAngle = Number(this.motion.openAngleDeg);
    return Number.isFinite(contractAngle) && contractAngle > 0
      ? THREE.MathUtils.degToRad(Math.min(125, Math.max(15, contractAngle)))
      : Math.PI / 2 * 0.97;
  }

  drawerOpenMagnitude(size: THREE.Vector3, depthAxisIdx: AxisIdx): number {
    const contractOpen = Number(this.motion.openDistanceMm);
    return Number.isFinite(contractOpen) && contractOpen > 0
      ? Math.min(contractOpen, 1200)
      : Math.max(180, Math.max(size.x, size.z) * 0.65, size.getComponent(depthAxisIdx) * 0.85);
  }

  /** Same reviewed semantics, expressed once in world coordinates. Module
   * projection is already owned above; source parent shear must never pass
   * through quaternion decomposition to choose a world axis. */
  animationWorldPlan(mesh: THREE.Object3D, worldSize: THREE.Vector3): FurnitureAnimationPlan | null {
    const plan = this.animationPlan(new THREE.Object3D(), mesh, worldSize);
    if (!plan) return null;
    const base = partMotionBaseFrame(mesh);
    if (plan.kind === 'drawer') plan.anchorWorld = new THREE.Vector3().setFromMatrixPosition(base.world);
    if (this.usesModuleSpace || !mesh.parent) return plan;
    const linear = new THREE.Matrix3().setFromMatrix4(base.parentWorld);
    if (plan.rotationAxis) plan.rotationAxis.applyMatrix3(linear).normalize();
    if (plan.translationAxis) plan.translationAxis.applyMatrix3(linear).normalize();
    return plan;
  }

  /** Gesture and rendering share the same reviewed world plan and current
   * closed module delta; a saved module move must also move its hinge. */
  animationCurrentWorldPlan(mesh: THREE.Object3D, worldSize: THREE.Vector3): FurnitureAnimationPlan | null {
    const plan = this.animationWorldPlan(mesh, worldSize);
    if (!plan) return null;
    const delta = partMotionModuleDelta(mesh);
    plan.anchorWorld?.applyMatrix4(delta);
    plan.rotationAxis?.transformDirection(delta);
    plan.translationAxis?.transformDirection(delta);
    return plan;
  }

  animationPlan(parent: THREE.Object3D, mesh: THREE.Object3D, worldSize: THREE.Vector3): FurnitureAnimationPlan | null {
    const capability = this.capability();
    const kind = capability.ok ? capability.kind : null;
    if (!kind || worldSize.lengthSq() < 1) return null;
    const direction = this.direction;
    if (!direction) return null;

    if (kind === 'door') {
      const hingeSide = String(this.motion.hingeSide || '').toLowerCase();
      const isLift = this.isLiftDoor();
      const isDropDown = this.isDropDownDoor();
      if (!isLift && !isDropDown && hingeSide !== 'left' && hingeSide !== 'right') return null;
      const axisIdx = this.axisIdx(this.motion.hingeAxis, (isLift || isDropDown) ? 0 : 1);
      const anchorWorld = this.pivotWorld();
      if (!anchorWorld) return null;
      return {
        kind,
        axisIdx,
        rotationAxis: this.axisInParent(parent, axisIdx),
        translationAxis: null,
        anchorWorld,
        openMagnitude: this.doorOpenMagnitude(),
        direction,
      };
    }

    const rawAxis = this.motion.slideAxis ?? this.motion.localSlideAxis;
    if (typeof rawAxis !== 'string' || !rawAxis.trim()) return null;
    const axisIdx = this.axisIdx(rawAxis, 2);
    mesh.updateWorldMatrix(true, false);
    const anchorWorld = new THREE.Vector3();
    mesh.getWorldPosition(anchorWorld);
    return {
      kind,
      axisIdx,
      rotationAxis: null,
      translationAxis: this.axisInParent(parent, axisIdx),
      anchorWorld,
      openMagnitude: this.drawerOpenMagnitude(worldSize, axisIdx),
      direction,
    };
  }
}
